import React from 'react';
import {
	Button,
	Divider,
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	ExtendedTheme,
	InlineText,
	Option
} from 'neon-design-system';
import { createStyles, makeStyles } from '@material-ui/core';
import { ArticleChangeHookType, ArticleState, ResearchRole } from '../../../shared/global-enums';
import { ArticleInfo, CategoryItemNode, Channel, User } from '../../../shared/types';
import ArticleDetails from './ArticleDetails';
import { useUser } from '../../providers/UserProvider';
import ArticleTaxonomies from './ArticleTaxonomies';
import { ArticleValidationError } from '../../../utils/validator.utils';
import { formatPublishDate } from '../../../utils/date.utils';

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	mainSideBar: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		flex: 1
	},
	sideBarContent: {
		bottom: 72,
		flex: 1
	},
	expansionPanel: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		overflow: 'visible'
	},
	details: {
		backgroundColor: theme.palette.grey[800]
	},
	bottomPanel: {
		display: 'flex',
		justifyContent: 'space-between'
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'column',
		width: '100%',
		backgroundColor: theme.themeColors.grey[700]
	},
	headerActions: {
		display: 'flex',
		justifyContent: 'flex-start',
		width: '100%',
		padding: theme.spacing(2)
	},
	hidden: {
		display: 'none'
	},
	status: {
		width: '100%',
		padding: theme.spacing(2)
	},
	statusRow: {
		marginBottom: theme.spacing(1),
		'&:last-child': {
			marginBottom: 0
		}
	},
	divider: {
		width: '100%'
	},
	statusText: {
		marginLeft: theme.spacing(1),
		fontWeight: 'bold'
	},
	headerMainAction: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		padding: theme.spacing(2)
	},
	buttonPublish: {
		flexBasis: '50%'
	},
	buttonSave: {
		marginLeft: theme.spacing(1),
		flexBasis: '50%'
	},
	buttonSaveOnly: {
		width: '100%',
		height: 40
	},
	bottomPanelLeft: {
		display: 'flex',
		alignItems: 'center',
		width: '50%',
		justifyContent: 'left'
	},
	bottomPanelRight: {
		display: 'flex',
		alignItems: 'center',
		width: 128,
		justifyContent: 'center'
	},
	buttonDelete: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	switchAutosave: {
		marginLeft: theme.spacing(2),
		width: 32,
		maxWidth: 32,
		height: 16
	},
	autosaveHeader: {
		marginLeft: theme.spacing(1)
	}
}));

interface ButtonLabels {
	save: string;
	publish: string;
}

export interface ArticleSideBarProps {
	isNew: boolean;
	selectedContributors: string[];
	detailSummary: string;
	publishDate?: Date;
	selectedCategories: string[];
	selectedTags: string[];
	openToAll: boolean;
	expiryOffset: string;
	selectedChannel?: Channel;
	showWatermark: boolean;
	displayInChartSection: boolean;
	isPinned: boolean;
	isTimeless: boolean;
	userHasTableuAccess: boolean;

	listUsers: User[];
	preparedChannelsList: Channel[];
	treeCategories: CategoryItemNode[];
	listTags: Option[];

	formBusy: boolean;

	articleInfo: ArticleInfo;

	errorList: ArticleValidationError[];

	deleteDraft: () => void;
	archiveArticle: () => void;
	publishArticle: () => void;
	onChangeValue: (changeType: ArticleChangeHookType, value: string | string[] | boolean) => void;
	onChangeMultiValue: (changeType: ArticleChangeHookType, value: Option[]) => void;
}

export const ArticleSideBar = (props: ArticleSideBarProps) => {
	const classes = useStyles();
	const { hasScope } = useUser();

	const createDetails = () =>
		<ArticleDetails
			selectedContributors={props.selectedContributors}
			detailSummary={props.detailSummary}
			publishDate={props.publishDate}
			listUsers={props.listUsers}
			canPublish={canPublish}
			errorList={props.errorList}
			openToAll={props.openToAll}
			showWatermark={props.showWatermark}
			displayInChartSection={props.displayInChartSection}
			isPinned={props.isPinned}
			isTimeless={props.isTimeless}
			userHasTableuAccess={props.userHasTableuAccess}
			selectedChannel={props.selectedChannel}
			preparedChannelsList={props.preparedChannelsList}
			selectedOffset={props.expiryOffset}
			onChangeValue={props.onChangeValue}
		/>;

	const createTaxonomies = () =>
		<ArticleTaxonomies
			selectedCategories={props.selectedCategories}
			selectedTags={props.selectedTags}

			treeCategories={props.treeCategories}
			listTags={props.listTags}

			errorList={props.errorList}

			onChangeValue={props.onChangeValue}
			onChangeMultiValue={props.onChangeMultiValue}
		/>;

	const canPublish = hasScope(ResearchRole.Editor) &&
		(props.isNew ||
			(props.articleInfo.articleState === ArticleState.Pending
				|| props.articleInfo.articleState === ArticleState.Published)
		);


	const articleStatus = props.articleInfo.articleState ?? ArticleState.Pending;
	const articleCreateDate = formatPublishDate(props.articleInfo.createDate, true);
	const articleEditDate = formatPublishDate(props.articleInfo.updateDate, true);
	const isArchived = props.articleInfo.articleState === ArticleState.Archived;

	const labels = getLabels(props.isNew, articleStatus);

	return (
		<div className={classes.mainSideBar}>
			<div className={classes.header}>
				<div className={classes.headerMainAction}>
					{canPublish && <Button
						className={classes.buttonPublish}
						busy={props.formBusy}
						id="article-publish-button"
						colorType="primary"
						size="large"
						onClick={props.publishArticle}
						type="button">{labels.publish}</Button>}
					<Button
						className={canPublish ? classes.buttonSave : classes.buttonSaveOnly}
						busy={props.formBusy}
						id="article-submit-button"
						colorType={canPublish ? 'grey' : 'primary'}
						size="large"
						type="submit">{labels.save}</Button>
				</div>
				<Divider className={classes.divider} />
				<div className={props.isNew ? classes.hidden : classes.status}>
					<div className={classes.statusRow}>
						<InlineText styleName="footnote">Status:</InlineText>
						<InlineText styleName="footnote" className={classes.statusText}>{articleStatus}</InlineText>
					</div>
					<div className={classes.statusRow}>
						<InlineText styleName="footnote">Last edit:</InlineText>
						<InlineText styleName="footnote" className={classes.statusText}>{articleEditDate}</InlineText>
					</div>
					<div className={classes.statusRow}>
						<InlineText styleName="footnote">Created:</InlineText>
						<InlineText styleName="footnote" className={classes.statusText}>{articleCreateDate}</InlineText>
					</div>
				</div>
				<Divider className={classes.divider} />
				{(!props.isNew && !isArchived) ? <>
					<div className={classes.headerActions}>
						<Button
							id="article-archive-button"
							busy={props.formBusy}
							type="button"
							onClick={props.archiveArticle}
							colorType="grey"
							size="small">Archive article</Button>
					</div>
					<Divider className={classes.divider} />
				</> : null}
			</div>
			<div className={classes.sideBarContent}>
				<ExpansionPanel
					className={classes.expansionPanel}
					defaultExpanded={true}>
					<ExpansionPanelSummary>Article details</ExpansionPanelSummary>
					<ExpansionPanelDetails className={classes.details}>{createDetails()}</ExpansionPanelDetails>
				</ExpansionPanel>
				<ExpansionPanel
					className={classes.expansionPanel}
					defaultExpanded={true}>
					<ExpansionPanelSummary>Taxonomies</ExpansionPanelSummary>
					<ExpansionPanelDetails className={classes.details}>{createTaxonomies()}</ExpansionPanelDetails>
				</ExpansionPanel>
			</div>
		</div>
	);

};

function getLabels(isNew: boolean, state: ArticleState): ButtonLabels {

	if (isNew) {
		return {
			save: 'Save to draft',
			publish: 'Publish'
		};
	}

	if (state === ArticleState.Published) {
		return {
			save: 'Convert to draft',
			publish: 'Update'
		};
	}

	if(state === ArticleState.Archived) {
		return {
			save: 'Update and unarchive',
			publish: 'Publish'
		};
	}

	return {
		save: 'Update draft',
		publish: 'Publish'
	};
}

export default ArticleSideBar;
